import React from 'react'
import { cn } from '@/utils/cn'

type ButtonVariant = 'brand' | 'ghost' | 'outline' | 'danger' | 'success'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
  brand:
    'bg-[var(--color-brand)] text-white border-transparent hover:bg-[var(--color-brand-hover)] shadow-[0_2px_8px_rgba(var(--color-brand-rgb),0.3)] hover:shadow-[0_4px_16px_rgba(var(--color-brand-rgb),0.4)]',
  ghost:
    'bg-transparent border-transparent text-[var(--text-secondary)] hover:bg-[var(--glass-medium)] hover:text-[var(--text-primary)]',
  outline:
    'bg-transparent border-[var(--border-medium)] text-[var(--text-primary)] hover:bg-[var(--glass-light)]',
  danger:
    'bg-[var(--color-error)] text-white border-transparent hover:bg-red-600 shadow-[0_2px_8px_rgba(239,68,68,0.3)]',
  success:
    'bg-[var(--color-success)] text-white border-transparent hover:bg-emerald-600 shadow-[0_2px_8px_rgba(16,185,129,0.3)]',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
}

export function Button({
  variant = 'brand',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  children,
  disabled,
  className,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center font-semibold rounded-[var(--radius-md)] border',
        'transition-all duration-150 cursor-pointer select-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        'active:scale-[0.97]',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <span
          className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
          aria-hidden="true"
        />
      ) : (
        icon && iconPosition === 'left' && (
          <span className="shrink-0">{icon}</span>
        )
      )}
      {children}
      {!loading && icon && iconPosition === 'right' && (
        <span className="shrink-0">{icon}</span>
      )}
    </button>
  )
}
